import { useEffect, useState, type ReactNode } from "react";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import type { ChannelCatalogItem, ConnectionHubError, CoverView, MailRule } from "../../../api/connectionHub.types";
import type { ProviderSetupGuide } from "../../../constants/providerSetupGuides.constants";
import { isRuleConfirmed as isRuleConfirmedOf, ruleConnectionIds as ruleConnectionIdsOf } from "../../../utils/outlookRules";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import PhaseActions from "../PhaseActions/PhaseActions";
import MailRuleFields from "./MailRuleFields";
import StepsAccordion from "../StepsAccordion/StepsAccordion";
import styles from "./OutlookRulesPanel.module.scss";

export interface OutlookRulesPanelProps {
  error?: ConnectionHubError | string | null;
  rules: MailRule[];
  covers: CoverView[];
  channelsByIpId: Map<string, ChannelCatalogItem>;
  guide?: ProviderSetupGuide;
  accountEmail: string;
  settingsUrl: string | null;
  resetKey?: string;
  canOpen?: boolean;
  canCopy?: boolean;
  /** CONFIRM_FORWARDING from the inbox's actions; without it the rule buttons stay disabled. */
  canConfirm?: boolean;
  disabled?: boolean;
  busy?: boolean;
  /** Replaces the default copy above the steps (the delegate frames word it differently). */
  headline?: string;
  /** Delegate frames drop a rule once it is confirmed instead of keeping a disabled "Rule Confirmed". */
  hideConfirmedRules?: boolean;
  /** Rendered after the rule cards — the delegate page lists the channels already verifying here. */
  afterRules?: ReactNode;
  appearance?: "default" | "primary";
  confirmDismissible?: boolean;
  continueAppearance?: "primary" | "secondary";
  leading?: ReactNode;
  onOpen: () => void;
  /** Confirms the channels one rule redirects; an empty list confirms the whole inbox. */
  onConfirmRule: (connectionIds: string[]) => void;
}

/** Outlook cannot prefill a rule, so the copy counts rules rather than channels. */
const headline = (rules: MailRule[], coverCount: number): string => {
  if (rules.length > 1) return `Add ${rules.length} separate rules to set up these channels.`;
  return coverCount > 1 ? "Add a rule to set up these channels." : "Add a rule to set up this channel.";
};

const OutlookRulesPanel = ({
  error,
  rules,
  covers,
  channelsByIpId,
  guide,
  accountEmail,
  settingsUrl,
  resetKey,
  canOpen = true,
  canCopy = false,
  canConfirm = true,
  disabled = false,
  busy = false,
  headline: headlineOverride,
  hideConfirmedRules = false,
  afterRules,
  appearance,
  confirmDismissible,
  continueAppearance,
  leading,
  onOpen,
  onConfirmRule,
}: OutlookRulesPanelProps): JSX.Element => {
  // Which rule's request is in flight, so only its button spins.
  const [pendingRule, setPendingRule] = useState<string | null>(null);

  useEffect(() => { if (!busy) setPendingRule(null); }, [busy]);

  const ruleConnectionIds = (rule: MailRule): string[] => ruleConnectionIdsOf(rule, covers);
  const isRuleConfirmed = (rule: MailRule): boolean => isRuleConfirmedOf(rule, covers);

  const confirmRule = (rule: MailRule): void => {
    const connectionIds = ruleConnectionIds(rule);
    if (!connectionIds.length) return;
    setPendingRule(rule.name);
    onConfirmRule(connectionIds);
  };

  // Numbered against the full list, so "Rule 2" keeps its name after Rule 1 is confirmed and hidden.
  const visibleRules = rules
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => !hideConfirmedRules || !isRuleConfirmed(rule));

  const ruleCards = (
    <div className={styles.rules}>
      {visibleRules.map(({ rule, index }) => (
        <article className={styles.rule} key={rule.name}>
          <header className={styles.ruleHeader}>
            <span className={styles.ruleLabel}>{rules.length > 1 ? `Rule ${index + 1}` : "Rule"}</span>
            <span className={styles.divider} aria-hidden="true">|</span>
            <span className={styles.covers}>
              <span>Covers</span>
              {rule.coveredIpIds.map((ipId) => {
                const cover = covers.find((item) => item.ipId === ipId);
                const channel = channelsByIpId.get(ipId);
                return (
                  <span className={styles.cover} key={ipId}>
                    <ConnectionLogo logoUrl={channel?.logo ?? cover?.logo} className={styles.coverLogo} />
                    <TruncateTooltip text={cover?.ipName ?? channel?.ipName ?? ipId} className={styles.coverName} />
                  </span>
                );
              })}
            </span>
          </header>

          <MailRuleFields rule={rule} />

          <MeritoButton
            type="secondary-2"
            size="md"
            className={styles.action}
            btnText={isRuleConfirmed(rule) ? "Rule Confirmed" : "I’ve Created This Rule"}
            loading={busy && pendingRule === rule.name}
            showLoadingWithText
            disabled={disabled || !canConfirm || isRuleConfirmed(rule) || !ruleConnectionIds(rule).length}
            onClick={() => confirmRule(rule)}
          />
        </article>
      ))}
      {afterRules}
    </div>
  );

  return (
    <div className={styles.panel}>
      {error ? <ConnectionErrorPanel error={error} /> : <p className={styles.sentence}>{headlineOverride ?? headline(rules, covers.length)}</p>}
      {guide ? (
        <StepsAccordion
          steps={guide.forwarding}
          {...(guide.notice ? { notice: guide.notice } : {})}
          videoUrl={guide.forwardingVideoUrl}
          phase="forwarding"
          providerName={guide.displayName}
        />
      ) : null}
      <PhaseActions
        providerName="Outlook"
        accountEmail={accountEmail}
        settingsUrl={settingsUrl}
        settingsLabel="Open Outlook Rules"
        confirmLabel="I’ve Created This Rule"
        declineLabel=""
        confirmNote="Make sure you're signed into this account in your browser. If not, copy the link and paste it where that account is signed in."
        confirmSlot={rules.length ? ruleCards : null}
        canOpen={canOpen}
        canCopy={canCopy}
        canConfirm={canConfirm}
        disabled={disabled}
        busy={busy}
        {...(appearance ? { appearance } : {})}
        {...(confirmDismissible ? { confirmDismissible } : {})}
        {...(continueAppearance ? { continueAppearance } : {})}
        leading={leading}
        {...(resetKey ? { resetKey } : {})}
        onOpen={onOpen}
        // Only reached when there are no rule cards to confirm one by one; an empty list means the whole inbox.
        onConfirm={() => onConfirmRule([])}
      />
    </div>
  );
};

export default OutlookRulesPanel;
